import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

interface Partner {
  id: string;
  partnerCode: string;
  businessName: string;
  emailLogin: string;
  status: string;
}

interface PartnerAuthContextType {
  partner: Partner | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  isAuthenticated: boolean;
}

const PartnerAuthContext = createContext<PartnerAuthContextType | undefined>(undefined);

export const usePartnerAuth = () => {
  const context = useContext(PartnerAuthContext);
  if (!context) {
    throw new Error('usePartnerAuth must be used within PartnerAuthProvider');
  }
  return context;
};

interface PartnerAuthProviderProps {
  children: ReactNode;
}

export const PartnerAuthProvider: React.FC<PartnerAuthProviderProps> = ({ children }) => {
  const [partner, setPartner] = useState<Partner | null>(null);
  const [loading, setLoading] = useState(true);

  // Load partner from localStorage on mount
  useEffect(() => {
    const loadPartner = async () => {
      try {
        const storedPartner = localStorage.getItem('partner_session');
        if (storedPartner) {
          const partnerData = JSON.parse(storedPartner);
          
          // Validate session by checking if partner still exists and is active
          const { data, error } = await supabase
            .rpc('get_partner_by_email', { login_email: partnerData.emailLogin });
          
          if (error || !data || data.length === 0) {
            // Session invalid - clear it
            localStorage.removeItem('partner_session');
            setPartner(null);
          } else {
            // Session valid - use stored data
            setPartner(partnerData);
          }
        }
      } catch (error) {
        console.error('Error loading partner session:', error);
        localStorage.removeItem('partner_session');
        setPartner(null);
      } finally {
        setLoading(false);
      }
    };

    loadPartner();
  }, []);

  const signIn = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setLoading(true);
      
      // Call independent authentication function
      const { data, error } = await supabase
        .rpc('authenticate_partner', {
          login_email: email.toLowerCase().trim(),
          login_password: password
        });

      if (error) {
        console.error('Partner authentication error:', error);
        return { 
          success: false, 
          error: 'Authentication failed. Please check your credentials.' 
        };
      }

      // Check if authentication was successful
      const authResult = data?.[0];
      if (!authResult?.success) {
        return { 
          success: false, 
          error: authResult?.message || 'Invalid email or password' 
        };
      }

      // Store partner session
      const partnerData: Partner = {
        id: authResult.partner_id,
        partnerCode: authResult.partner_code,
        businessName: authResult.business_name,
        emailLogin: email.toLowerCase().trim(),
        status: authResult.status
      };

      localStorage.setItem('partner_session', JSON.stringify(partnerData));
      setPartner(partnerData);

      return { success: true };
    } catch (error) {
      console.error('Unexpected authentication error:', error);
      return { 
        success: false, 
        error: 'An unexpected error occurred. Please try again.' 
      };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      localStorage.removeItem('partner_session');
      setPartner(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value: PartnerAuthContextType = {
    partner,
    loading,
    signIn,
    signOut,
    isAuthenticated: !!partner
  };

  return (
    <PartnerAuthContext.Provider value={value}>
      {children}
    </PartnerAuthContext.Provider>
  );
};